import React, { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { wpClient } from '../lib/wordpress';

const SEARCH_DISABILITY_HACKS = gql`
  query SearchDisabilityHacks($first: Int!, $after: String, $search: String) {
    disabilityHacks(
      first: $first,
      after: $after,
      where: { search: $search }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        databaseId
        title
        categories {
          nodes {
            name
          }
        }
        disabilityHacksImport {
          title
          platform
          videoUrl
          summary
          hackDescription
          otKeywords {
            name
          }
          source
          userTested
          otUsefulnessRating
          hackType
          reviewedBy
          difficultyLevel
          featured
          submittedByEmail
        }
      }
    }
  }
`;

interface SearchResult {
  databaseId: number;
  title: string;
  categories: {
    nodes: { name: string }[];
  };
  disabilityHacksImport: {
    title: string;
    platform: string;
    videoUrl: string;
    summary: string;
    hackDescription: string;
    otKeywords: { name: string }[];
    source: string;
    userTested: string;
    otUsefulnessRating: number;
    hackType: string;
    reviewedBy: string;
    difficultyLevel: string;
    featured: boolean;
    submittedByEmail: string;
  };
}

interface HackSearchProps {
  isHomePage?: boolean;
}

export function HackSearch({ isHomePage = false }: HackSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();

  // Get search term from URL if on browse page
  useEffect(() => {
    if (!isHomePage) {
      const searchParams = new URLSearchParams(location.search);
      const urlSearchTerm = searchParams.get('search') || '';
      setSearchTerm(urlSearchTerm);
    }
  }, [location.search, isHomePage]);

  const { loading, error, data, fetchMore } = useQuery(SEARCH_DISABILITY_HACKS, {
    variables: {
      first: 10,
      search: searchTerm
    },
    skip: isHomePage
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setPage(1);

    if (isHomePage) {
      navigate(`/browse?search=${encodeURIComponent(newSearchTerm)}`);
    } else {
      const searchParams = new URLSearchParams(location.search);
      if (newSearchTerm) {
        searchParams.set('search', newSearchTerm);
      } else {
        searchParams.delete('search');
      }
      navigate({ search: searchParams.toString() });
    }
  };

  const loadMore = () => {
    if (data?.disabilityHacks?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data.disabilityHacks.pageInfo.endCursor,
          first: 10,
          search: searchTerm
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            disabilityHacks: {
              ...fetchMoreResult.disabilityHacks,
              nodes: [
                ...prev.disabilityHacks.nodes,
                ...fetchMoreResult.disabilityHacks.nodes
              ]
            }
          };
        }
      });
      setPage(prev => prev + 1);
    }
  };

  // Filter hacks based on search term
  const filteredHacks = data?.disabilityHacks?.nodes.filter((hack: SearchResult) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      hack.title?.toLowerCase().includes(searchLower) ||
      hack.disabilityHacksImport?.hackDescription?.toLowerCase().includes(searchLower) ||
      hack.disabilityHacksImport?.otKeywords?.some(keyword => keyword.name.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="mb-8">
        <input
          type="text"
          placeholder="Search disability hacks..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full px-4 py-2 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {!isHomePage && (
        <>
          {loading && <p className="text-gray-600">Loading hacks...</p>}
          {error && <p className="text-red-600">Error: {error.message}</p>}
          
          {filteredHacks && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHacks.map((hack: SearchResult) => (
                  <Link
                    key={hack.databaseId}
                    to={`/hack/${hack.databaseId}`}
                    className="block bg-[#f5f7f5] rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="p-6 flex flex-col h-full">
                      <div className="text-green-700 font-medium mb-2 uppercase text-sm">
                        {hack.categories?.nodes.map(node => node.name).join(', ') || 'Uncategorized'}
                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {hack.title}
                      </h3>

                      <p className="text-gray-600 mb-4 flex-grow">
                        {hack.disabilityHacksImport?.hackDescription}
                      </p>

                      {hack.disabilityHacksImport?.otKeywords && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {hack.disabilityHacksImport.otKeywords.map(keyword => (
                            <span
                              key={keyword.name}
                              className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm"
                            >
                              {keyword.name}
                            </span>
                          ))}
                        </div>
                      )}

                      <button className="w-full border-2 border-gray-900 text-gray-900 rounded-lg py-2 px-4 hover:bg-gray-900 hover:text-white transition-colors">
                        View Details
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
              
              {data?.disabilityHacks?.pageInfo?.hasNextPage && (
                <div className="mt-8 text-center">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
} 